import { Vector3 } from 'three';

/**
 * Declarative support primitives — core emits these, supportMesh.ts turns them
 * into tubes. (Named TrackSupport-* to stay clear of future PrintSupport, §11.5.)
 */
export type TrackSupportPrimitive =
  | { kind: 'footer'; pos: Vector3; radius: number; height: number }
  | { kind: 'column'; a: Vector3; b: Vector3; radius: number; strategy: PlacementStrategy }
  | { kind: 'brace'; a: Vector3; b: Vector3; radius: number }
  | { kind: 'knee'; a: Vector3; b: Vector3; radius: number }
  /** Conical hanger: `a` at the track, `b` at the elbow. `attachS` identifies the station for picking. */
  | { kind: 'stub'; a: Vector3; b: Vector3; radiusA: number; radiusB: number; attachS?: number }
  /** Fills the angle between stub and column so the solid has no gap for printing. */
  | { kind: 'joint'; pos: Vector3; radius: number };

export type PlacementStrategy = 'vertical' | 'leaning' | 'aframe' | 'knee' | 'forced';

/** Manual pins / suppressions / foot moves persisted on the coaster document. */
export type SupportOverride =
  | { kind: 'force'; s: number }
  | { kind: 'suppress'; from: number; to: number }
  /** Pin a ground column foot to plan (x,z); Y comes from terrain. */
  | { kind: 'forceFoot'; s: number; x: number; z: number; role?: 'main' | 'triangle' }
  /** Force two or more ground feet onto one shared pad (ignores merge distance / triangle rules). */
  | {
      kind: 'mergeFeet';
      sA: number;
      roleA?: 'main' | 'triangle';
      sB: number;
      roleB?: 'main' | 'triangle';
      extra?: { s: number; role?: 'main' | 'triangle' }[];
    };

export type MergeFeetOverride = Extract<SupportOverride, { kind: 'mergeFeet' }>;

/** All feet in a mergeFeet override, including optional extras beyond the pair. */
export function mergeFeetMembers(
  o: MergeFeetOverride,
): { s: number; role: 'main' | 'triangle' }[] {
  const members: { s: number; role: 'main' | 'triangle' }[] = [
    { s: o.sA, role: o.roleA ?? 'main' },
    { s: o.sB, role: o.roleB ?? 'main' },
  ];
  for (const m of o.extra ?? []) {
    members.push({ s: m.s, role: m.role ?? 'main' });
  }
  return members;
}

/** Ground foot emitted by the solver — used for picking / forcing base points. */
export interface SupportFoot {
  attachS: number;
  role: 'main' | 'triangle';
  pos: Vector3;
}

export interface SupportResult {
  primitives: TrackSupportPrimitive[];
  /** Ground feet after merge + forceFoot overrides (one per placed column). */
  feet: SupportFoot[];
  /** attach arc lengths that ended up unsupported (span carried by neighbours) */
  skipped: number[];
  warnings: string[];
}
